import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  normalizeMeasurementType, toStoredMeasurementType,
  typeDefaultThreshold, actionThresholdDescriptor,
  type SpecMeasurementType, type SamplingUnit, type TallyThreshold, type Category,
  type ActionThresholdDescriptor,
} from './pestConfig';

export interface ApiPest {
  _id: string;
  code: string;
  name: string;
  measurementType?: string;
  samplingUnit?: SamplingUnit;
  tallyThreshold?: TallyThreshold;
  actionThreshold?: number;
  category?: Category;
}

export interface Draft {
  measurementType: SpecMeasurementType;
  samplingUnit: SamplingUnit;
  tallyThreshold: TallyThreshold;
  actionThreshold: number;
  category: Category;
}

interface SavePayload {
  code: string;
  name: string;
  measurementType: string;
  actionThreshold: number;
  category: Category;
  samplingUnit?: SamplingUnit;
  tallyThreshold?: TallyThreshold;
}

export const toDraft = (p: ApiPest): Draft => ({
  measurementType: normalizeMeasurementType(p.measurementType),
  samplingUnit: p.samplingUnit ?? 'point',
  tallyThreshold: p.tallyThreshold ?? 'any',
  actionThreshold: typeof p.actionThreshold === 'number' ? p.actionThreshold : 20,
  category: p.category ?? 'pest',
});

export interface PestConfigEditor {
  pests: ApiPest[];
  selectedId: string | null;
  selected: ApiPest | null;
  draft: Draft | null;
  descriptor: ActionThresholdDescriptor | null;
  error: string | null;
  success: string | null;
  saving: boolean;
  select: (p: ApiPest) => void;
  changeType: (next: SpecMeasurementType) => void;
  patchDraft: (patch: Partial<Draft>) => void;
  adjustThreshold: (delta: number) => void;
  resetDraft: () => void;
  save: () => Promise<void>;
}

/** Shared Pest Config edit-state machine (desktop master–detail + mobile compact). */
export function usePestConfigEditor(): PestConfigEditor {
  const { t } = useTranslation();
  const [pests, setPests] = useState<ApiPest[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get('/api/v1/pests', { withCredentials: true });
      const data: ApiPest[] = res.data;
      setPests(data);
      if (!selectedId && data.length) {
        setSelectedId(data[0]._id);
        setDraft(toDraft(data[0]));
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('fieldScout.config.loadError'));
    }
  }, [selectedId, t]);

  useEffect(() => { load(); }, [load]);

  const select = useCallback((p: ApiPest) => {
    setSelectedId(p._id);
    setDraft(toDraft(p));
    setSuccess(null);
    setError(null);
  }, []);

  const changeType = useCallback((next: SpecMeasurementType) => {
    setDraft(d => d && ({ ...d, measurementType: next, actionThreshold: typeDefaultThreshold(next) }));
  }, []);

  const patchDraft = useCallback((patch: Partial<Draft>) => {
    setDraft(d => d && ({ ...d, ...patch }));
  }, []);

  const adjustThreshold = useCallback((delta: number) => {
    setDraft(d => {
      if (!d) return d;
      const dsc = actionThresholdDescriptor(d.measurementType);
      const next = Math.min(dsc.max, Math.max(dsc.min, d.actionThreshold + delta));
      return { ...d, actionThreshold: next };
    });
  }, []);

  const selected = selectedId ? pests.find(p => p._id === selectedId) ?? null : null;

  const resetDraft = useCallback(() => {
    // no-op when no pest is selected (Cancel is only rendered when one is)
    if (selected) setDraft(toDraft(selected));
  }, [selected]);

  const save = useCallback(async () => {
    if (!selectedId || !draft) return;
    const sel = pests.find(p => p._id === selectedId);
    if (!sel) return;
    setSaving(true); setError(null);
    try {
      const isIncidence = draft.measurementType === 'incidence';
      const payload: SavePayload = {
        code: sel.code,
        name: sel.name,
        measurementType: toStoredMeasurementType(draft.measurementType),
        actionThreshold: draft.actionThreshold,
        category: draft.category,
        ...(isIncidence && { samplingUnit: draft.samplingUnit, tallyThreshold: draft.tallyThreshold }),
      };
      await axios.put(`/api/v1/pests/${selectedId}`, payload, { withCredentials: true });
      setSuccess(t('fieldScout.config.saved'));
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || t('fieldScout.config.saveError'));
    } finally {
      setSaving(false);
    }
  }, [selectedId, draft, pests, t, load]);

  const descriptor = draft ? actionThresholdDescriptor(draft.measurementType) : null;

  return {
    pests, selectedId, selected, draft, descriptor, error, success, saving,
    select, changeType, patchDraft, adjustThreshold, resetDraft, save,
  };
}
